import { createTheme } from '@mui/material';
import { ptBR as corePtBR } from '@mui/material/locale';
import { ptBR as dpPtBR } from '@mui/x-date-pickers/locales';

import { createSeencPalette } from './palette';
import property from './properties';
import shadows from './shadows';
import createTypography from './typography';

export function createSeencTheme() {
  const palette = createSeencPalette();
  const baseTheme = createTheme({
    palette,
  });
  const typography = createTypography(baseTheme);

  return createTheme(
    {
      breakpoints: {
        values: {
          xs: 0,
          sm: 600,
          md: 900,
          lg: 1200,
          xl: 1536,
        },
      },
      cssVariables: {
        nativeColor: true,
      },
      palette,
      shadows,
      typography,
      custom: {
        borderRadius: {
          default: property.cssBorderRadius.seencDefault,
          dashboard: property.cssBorderRadius.seencDashboard,
        },
        padding: {
          default: property.cssPadding.seencDefault,
          menuLogo: property.cssPadding.seencMenuLogo,
        },
        gap: {
          default: property.cssGap.seencDefault,
        },
        height: {
          tableHeader: property.cssHeight.seencTableHeader,
          tableRow: property.cssHeight.seencTableRow,
        },
      },
      components: {
        MuiCheckbox: {
          styleOverrides: {
            root: {
              color: palette.secondary.main,
            },
          },
        },
        MuiLinearProgress: {
          styleOverrides: {
            root: {
              borderRadius: '5px',
            },
            barColorPrimary: {
              backgroundColor: palette.secondary.main,
            },
          },
        },
        //Seenc
        MuiButton: {
          styleOverrides: {
            root: {
              height: '40px',
              minHeight: '40px',
              maxHeight: '40px',
              borderRadius: property.cssBorderRadius.seencDefault,
            },
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            root: {
              padding: 0,
              margin: 0,
              height: '40px',
              borderRadius: property.cssBorderRadius.seencDefault,
              backgroundColor: palette.common.white,
              ':hover:not($disabled)': {
                backgroundColor: palette.background.default,
              },
              '& input:-webkit-autofill': {
                WebkitBoxShadow: '0 0 0 100px white inset',
                WebkitTextFillColor: '#000',
                transition: 'background-color 5000s ease-in-out 0s',
              },
            },
            input: {
              margin: 0,
              padding: '0 10px',
              height: '100%',
              fontFamily: 'Poppins, Open Sans',
              fontWeight: 500,
              fontSize: '13px',
              lineHeight: '40px',
              color: palette.text.main,
              '&:-webkit-autofill': {
                backgroundColor: `transparent !important`,
              },
              '&::placeholder': {
                color: palette.secondary.main,
                opacity: 0.6,
              },
            },
          },
        },
        MuiInputAdornment: {
          styleOverrides: {
            root: {
              margin: 0,
              padding: 0,
              paddingInline: 10,
              height: '100%',
              minHeight: '40px',
            },
            positionStart: {
              backgroundColor: palette.background[100],
              color: palette.secondary.main,
              '& .MuiTypography-root': {
                color: palette.secondary.main,
              },
              borderTopLeftRadius: property.cssBorderRadius.seencDefault,
              borderBottomLeftRadius: property.cssBorderRadius.seencDefault,
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
            },
            positionEnd: {
              backgroundColor: palette.background[100],
              color: palette.secondary.main,
              '& .MuiTypography-root': {
                color: palette.secondary.main,
              },
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
              borderTopRightRadius: property.cssBorderRadius.seencDefault,
              borderBottomRightRadius: property.cssBorderRadius.seencDefault,
            },
          },
        },
        MuiSelect: {
          styleOverrides: {
            select: {
              display: 'flex',
              alignItems: 'center',
              minHeight: '20px',
              padding: '11.5px 10px',
              fontFamily: 'Poppins, Open Sans',
              fontWeight: 500,
              fontSize: '13px',
              lineHeight: '13px',
              color: palette.text.main,
              '&.MuiSelect-multiple': {
                paddingTop: '11.5px',
                paddingBottom: '11.5px',
              },
            },
          },
        },
        MuiAutocomplete: {
          styleOverrides: {
            root: {
              padding: 0,
              margin: 0,
            },
            input: {
              margin: 0,
              padding: '0 10px !important',
              height: '40px',
              fontFamily: 'Poppins, Open Sans',
              fontWeight: 500,
              fontSize: '13px',
              lineHeight: '40px',
              color: palette.text.main,
              '&:-webkit-autofill': {
                backgroundColor: `transparent !important`,
                WebkitBoxShadow: '0 0 0 100px white inset',
                WebkitTextFillColor: '#000',
                transition: 'background-color 5000s ease-in-out 0s',
              },
              '&::placeholder': {
                color: palette.secondary.main,
                opacity: 0.6,
              },
            },
            inputRoot: {
              padding: '0 !important',
              height: '40px',
              "&[class*='MuiOutlinedInput-root']": {
                padding: '0 !important',
              },
            },
            endAdornment: {
              right: '9px !important',
            },
          },
        },
        MuiTab: {
          styleOverrides: {
            root: {
              ...typography.seenc_default,
            },
          },
        },
      },
      zIndex: {
        appBar: 1200,
        drawer: 1100,
      },
    },
    corePtBR,
    dpPtBR
  );
}
